import React, { memo, useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useLocation, Link } from 'react-router-dom';
import UserWayAccessibilityMenu from "../../UserWayAccessibilityMenu/index";
import SocialMediaIcons from '../../SocialMediaIcons/index';
import { isMobileOrTablet } from "../../Utils/index";
import './navbar.css';

const imgCDNUrl1 = process.env.REACT_APP_CDN;
const imgCDNUrl2 = process.env.REACT_APP_CDN_LINK;

// Memoized logo component to prevent unnecessary re-renders
const Logo = memo(({ imgCDNUrl }) => (
  <a href="/" className="logo-link">
    <div className="navbar-logo-container">
      <img
        src={imgCDNUrl1 + 'angl-points/assets/angl-token.webp'}
        alt="Angel Token Logo"
        className="logo-image"
        loading="lazy"
        width="50"
        height="50"
      />
      <p className="navbar-logo-text">ANGL TOKEN</p>
    </div>
  </a>
));

// Memoized button to prevent unnecessary re-renders
const WhitepaperButton = memo(() => (
  <Link
    // href="https://whitepaper.myangl.ai/"
    // target="_blank"
    // rel="noopener noreferrer"
    to="/whitepaper"
    className="create-button"
  >
    ANGL Whitepaper
  </Link>
));

const socialLinks = [
  {
    url: "https://www.dextools.io/app/en/token/angltoken?t=1751005709984",
    icon: "myanglai/Images/dextools-icon.webp",
    name: "DEXTools"
  },
  {
    url: "https://coinmarketcap.com/currencies/angel-twin/",
    icon: "myanglai/Images/coinmarketcap-icon.webp",
    name: "CoinMarketCap"
  },
  {
    url: "https://www.coingecko.com/en/coins/angel-twin",
    icon: "myanglai/Images/coinGecko-icon.webp",
    name: "CoinGecko"
  }
];

const FindUsOn = memo(() => (
  <div className="flex flex-wrap items-center gap-2 create-button">
    <span className="find-us-text">Find Us On :</span>
    {socialLinks.map((link, index) => (
      <a
        key={index}
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2"
      >
        <img src={imgCDNUrl2 + link.icon} alt={link.name} className="h-5 w-5 desktop-find-us-link" />
        <span className="find-us-icons">{link.name}</span>
      </a>
    ))}
  </div>
));

// Mobile Find Us On Component - Different layout for dropdown
const MobileFindUsOn = memo(() => (
  <div className="mobile-find-us-container">
    <div className="mobile-find-us-header">
      <span className="mobile-find-us-title">Find Us on</span>
    </div>
    <div className="mobile-find-us-links">
      {socialLinks.slice(0, -1).map((link, index) => (
        <a
          key={index}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mobile-find-us-link"
        >
          <img src={imgCDNUrl2 + link.icon} alt={link.name} className="mobile-find-us-icon" />
          <span className="mobile-find-us-text">{link.name}</span>
        </a>
      ))}
      <div className="mobile-find-us-last-link-row">
        <a
          href={socialLinks[socialLinks.length - 1].url}
          target="_blank"
          rel="noopener noreferrer"
          className="mobile-find-us-link"
        >
          <img src={imgCDNUrl2 + socialLinks[socialLinks.length - 1].icon} alt={socialLinks[socialLinks.length - 1].name} className="mobile-find-us-icon" />
          <span className="mobile-find-us-text">{socialLinks[socialLinks.length - 1].name}</span>
        </a>
        <div className="mobile-find-us-ada-row">
          <UserWayAccessibilityMenu color="white" width={35} height={35} />
        </div>
      </div>
    </div>
  </div>
));

// Hamburger Icon Component
const HamburgerIcon = memo(({ isOpen, onClick }) => (
  <button
    className="hamburger-icon"
    onClick={onClick}
    aria-label="Toggle menu"
    aria-expanded={isOpen}
  >
    <span className={`hamburger-line ${isOpen ? 'open' : ''}`}></span>
    <span className={`hamburger-line ${isOpen ? 'open' : ''}`}></span>
    <span className={`hamburger-line ${isOpen ? 'open' : ''}`}></span>
  </button>
));

// Mobile Dropdown Menu Component
const MobileDropdown = memo(({ isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="mobile-dropdown-overlay" onClick={onClose}>
      <div className="mobile-dropdown-content" onClick={e => e.stopPropagation()}>
        <MobileFindUsOn />
      </div>
    </div>
  );
});

const HeaderNavbar = () => {
  const location = useLocation();
  const imgCDNUrl = process.env.REACT_APP_CDN_LINK + "myanglai/wordpress/";
  const [navbar, setNavbar] = useState(false);
  const [scrollingDown, setScrollingDown] = useState(false);
  const isMobileOrTabletDevice = isMobileOrTablet();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    let prevScrollPos = window.scrollY;

    const handleScroll = () => {
      const currentScrollPos = window.scrollY;

      // Close mobile menu on scroll
      setIsMobileMenuOpen(false);

      // Set navbar visibility state based on scroll direction
      if (currentScrollPos <= 0) {
        // At the top of page
        setNavbar(false);
        setScrollingDown(false);
      } else if (prevScrollPos > currentScrollPos) {
        // Scrolling up
        setNavbar(true);
        setScrollingDown(false);
      } else if (prevScrollPos < currentScrollPos) {
        // Scrolling down
        if (!isMobileOrTabletDevice) {
          setNavbar(true);
          setScrollingDown(false);
        } else {
          setNavbar(false);
          setScrollingDown(true);
        }
      }

      prevScrollPos = currentScrollPos;
    };

    // Add the event listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Clean up
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobileOrTabletDevice]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const navbarClass = scrollingDown ?
    "nav-container scrolling-down" :
    navbar ? "nav-container active" : "nav-container";

  return (
    <>
      <header className="masthead">
        <div id="menuNavbar" className={navbarClass}>
          <div className="bar-top">
            <Container className="bar-content">
              <Row className="bar-container">
                <Col xs={3} lg={1} xl={2} md={2} className="nav-logo-container">
                  <Logo imgCDNUrl={imgCDNUrl} />
                </Col>

                {/* Desktop View */}
                <Col xs={9} md={9} lg={11} xl={10} className="button-container desktop">
                  <FindUsOn />
                  <a
                    href="https://www.angeltwin.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="featured-text-link"
                  >
                    <div className="featured-text d-none d-lg-flex">
                      Angel Twin Platform
                    </div>
                  </a>

                  <WhitepaperButton />

                  <div className="angl-desktop-social-icons">
                    <SocialMediaIcons />
                  </div>

                  <div className="desktop-accessibility-icon">
                    <UserWayAccessibilityMenu color="white" width={45} height={45} />
                  </div>
                </Col>

                {/* Mobile View */}
                <Col xs={9} lg={6} md={10} className="button-container mobile">
                  <a
                    href="https://www.angeltwin.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="featured-text-link"
                  >
                    <div className="featured-text d-lg-flex">
                      Angel Twin Platform
                    </div>
                  </a>
                  <WhitepaperButton />

                  <div className="angl-desktop-social-icons">
                    <SocialMediaIcons />
                  </div>

                  {/* Hamburger Menu Icon */}
                  <div className="mobile-hamburger-container">
                    <HamburgerIcon 
                      isOpen={isMobileMenuOpen} 
                      onClick={toggleMobileMenu}
                    />
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        </div>
      </header>

      {/* Mobile Dropdown Menu */}
      <MobileDropdown 
        isOpen={isMobileMenuOpen} 
        onClose={closeMobileMenu}
      />


    </>
  );
};

export default memo(HeaderNavbar);